import { EnviromentDataModel } from '@/_models/EnviromentModel'
import { Constant } from '@/_services/constant'
import { parseInfoToString, getBgColorCO2 } from '@/_utils/entity'
import { parserTimeByFormat, timeAgo } from './datetime'
const processingFlag: string = 'is-processing-pin'
const markerLayerId: string = 'SocanMarkerLayer'

const makeColorClass = color => {
  return color.replace('#', 'category-color-').toLowerCase()
}

const makePinSrc = configs => {
  const query =
    Object.keys(configs)
      .map(key => key + '=' + configs[key])
      .join('&') + '&'

  return ['/blank-35x60.png', query].join('?')
}

class IPinConfig {
  public type!: string // gps | beacon
  public isSelected: boolean = false
  public infowindowText?: string = ''
  public countNavi?: number = 0
  public hasAlert?: boolean = false
  public enviromentInfo?: string = ''
  public countPeople?: number = 0
  public peopleColor?: string = ''
  public countThings?: number = 0
  public thingColor?: string = ''
  public nearestUsers?: any[] = []
  public addtionClass?: string = ''
  public userType!: string // people | things
  public uniqueId!: string
}

export const prettyPinParseConfig = url => {
  const urlQuery = url.split('?')[1]
  const configs = new URLSearchParams(urlQuery)
  const isSelected = configs.get('isSelected') === 'true' ? true : false

  return {
    type: configs.get('type') || '',
    additionClass: configs.get('addtionClass') || '',
    isSelected,
    infowindowText: configs.get('addtionClass') || '',
    countNavi: Number(configs.get('countNavi')) || 0,
    hasAlert: configs.get('hasAlert') === 'true',
    enviromentInfo: configs.get('enviromentInfo') || '',
    countPeople: Number(configs.get('countPeople')) || 0,
    peopleColor: configs.get('peopleColor') || '',
    countThings: Number(configs.get('countThings')) || 0,
    thingColor: configs.get('thingColor') || '',
    userType: configs.get('userType') || '',
    uniqueId: configs.get('uniqueId') || 'unknownId',
  }
}

export const prettyPinConfig = (configs: IPinConfig) => {
  const nearestUsers = configs.nearestUsers || []
  delete configs.nearestUsers

  const things: any = []
  const navis: any = []
  const peoples: any = []
  const infowindowText: string[] = []

  configs.hasAlert = false

  if (nearestUsers.length) {
    for (const user of nearestUsers) {
      if (user.user_type === 'people') {
        peoples.push(user)
      } else if (user.user_type === Constant.NAVI_USER_TYPE) {
        navis.push(user)
      } else if (user.user_type === Constant.ENVIROMENT_USER_TYPE) {
        const envInfo = {
          ...user.enviroment_data,
          created_at: parserTimeByFormat(user.created_at),
          san_id: user.san_id,
        }
        configs.enviromentInfo =
          Object.keys(envInfo)
            .map(key => `${key}=${envInfo[key]}`)
            .join('--') || ''
      } else {
        things.push(user)
      }

      if (user.error_message) {
        configs.hasAlert = true
      }

      infowindowText.push(user.name)
    }
  }

  configs.infowindowText = infowindowText.join('+')
  configs.countPeople = peoples.length
  if (configs.countPeople === 1) {
    configs.peopleColor = peoples[0].category_color
  }

  configs.countNavi = navis.length

  configs.countThings = things.length
  if (configs.countThings === 1) {
    configs.thingColor = things[0].category_color
  }

  return makePinSrc(configs)
}

export const setPinColor2Selected = marker => {
  const src = marker.getIcon()
  const configs = prettyPinParseConfig(src)
  configs.isSelected = true
  marker.setIcon(makePinSrc(configs))
}

export const setPinColor2Default = marker => {
  const src = marker.getIcon()
  const configs = prettyPinParseConfig(src)
  configs.isSelected = false
  const existingEls = document.getElementsByClassName(configs.uniqueId)
  if (existingEls.length) {
    for (const el of existingEls) {
      el.parentNode?.removeChild(el)
    }
  }
  marker.setIcon(makePinSrc(configs))
}

export const prettyPin = (overlay, vmName) => {
  const pinName = vmName + markerLayerId
  overlay.getPanes().markerLayer.id = pinName
  overlay.getPanes().markerLayer.classList.add('SocanMarkerLayer')

  const socanMarkerLayer = document.getElementById(pinName)
  if (!socanMarkerLayer) {
    return
  }

  const config = { attributes: true, childList: true, subtree: true }

  const watchPinChange = (mutationsList, observer) => {
    const isProcessing = document.body.classList.contains(processingFlag)
    if (!socanMarkerLayer || isProcessing) {
      return
    }

    document.body.classList.add(processingFlag)
    const tmpNode = socanMarkerLayer.cloneNode(true) as HTMLElement
    const pins = tmpNode.getElementsByTagName('img')

    for (const pin of pins) {
      if (pin && pin.src) {
        const configs = prettyPinParseConfig(pin.src)
        pin.classList.add('pin-transparent-img')

        const pinParentNode = pin.parentNode as HTMLElement
        if (!pinParentNode) {
          continue
        }

        const existedSpan = pinParentNode.getElementsByTagName('span')
        if (existedSpan.length) {
          existedSpan[0].remove()
        }
        const prettyPinEle = document.createElement('div')
        // Each of pin hold a uniqueId, do not add duplicated pin
        prettyPinEle.classList.add(configs.uniqueId)
        prettyPinEle.classList.add('pretty-pin')

        // Is mixed people and things
        if (configs.countPeople > 0 && (configs.countThings > 0 || configs.countNavi > 0)) {
          prettyPinEle.classList.add('pretty-pin--mixed')
        }

        if (configs.type !== null) {
          prettyPinEle.classList.add('type-' + configs.type)
        }

        if (configs.isSelected) {
          prettyPinEle.classList.add('is-active')
        }

        if (configs.additionClass) {
          prettyPinEle.classList.add(configs.additionClass)
        }

        // Pepples
        if (configs.countPeople) {
          const pinHtml = document.createElement('div')
          pinHtml.classList.add('pretty-pin--infobox')

          if (configs.peopleColor) {
            pinHtml.classList.add(makeColorClass(configs.peopleColor))
          }

          pinHtml.classList.add('pretty-pin--infobox-people')
          if (configs.countPeople > 1) {
            pinHtml.innerHTML += '<span class="count">' + configs.countPeople + '</span>'
          }
          pinHtml.innerHTML += '<span class="mdi mdi-account user-icon"></span>'
          prettyPinEle.appendChild(pinHtml)
        }

        // Things
        if (configs.countThings) {
          const pinHtml = document.createElement('div')
          pinHtml.classList.add('pretty-pin--infobox')
          pinHtml.classList.add('pretty-pin--infobox-things')

          if (configs.thingColor) {
            pinHtml.classList.add(makeColorClass(configs.thingColor))
          }

          if (configs.countThings > 1) {
            pinHtml.innerHTML += '<span class="count">' + configs.countThings + '</span>'
          }
          pinHtml.innerHTML += '<span class="mdi mdi-package-variant-closed user-icon"></span>'
          prettyPinEle.appendChild(pinHtml)
        }

        // Navi
        if (configs.countNavi) {
          const pinHtml = document.createElement('div')
          pinHtml.classList.add('pretty-pin--infobox')
          pinHtml.classList.add('pretty-pin--infobox-navi')

          if (configs.countNavi > 1) {
            pinHtml.innerHTML += '<span class="count">' + configs.countNavi + '</span>'
          }
          pinHtml.innerHTML += '<span class="mdi mdi-needle user-icon"></span>'
          prettyPinEle.appendChild(pinHtml)
        }

        if (configs.hasAlert && configs.enviromentInfo) {
          const wrapHtml = document.createElement('span')
          wrapHtml.classList.add('is-flex', 'is-align-items-center', 'items-center-beacon')

          generate_alert(wrapHtml)
          generate_enviroment(wrapHtml, configs.enviromentInfo, overlay.vm.envInfoDisplays)
          prettyPinEle.appendChild(wrapHtml)
        } else {
          // hasAlert
          if (configs.hasAlert) {
            generate_alert(prettyPinEle)
          }

          // hasEvironmentInfo
          if (configs.enviromentInfo) {
            generate_enviroment(prettyPinEle, configs.enviromentInfo, overlay.vm.envInfoDisplays)
          }
        }

        if (configs.type === 'beacon') {
          prettyPinEle.innerHTML += '<span class="mdi mdi-hexagon beacon"></span>'
        }

        if (configs.type === 'beacon-camera') {
          prettyPinEle.innerHTML +=
            '<span class="mdi mdi-camera camera"></span> <span class="mdi mdi-hexagon beacon"></span>'
        }

        pinParentNode.appendChild(prettyPinEle)
      }
    }

    const markerLayer1 = document.getElementById(pinName)
    if (markerLayer1 && markerLayer1.parentNode) {
      markerLayer1.parentNode.replaceChild(tmpNode, markerLayer1)
    }

    if (vmName.includes('indoor')) {
      autoReCounterEnviromentTime()
    }

    document.body.classList.remove(processingFlag)
  }

  // Create an observer instance linked to the callback function
  overlay.observer = new MutationObserver(watchPinChange)
  overlay.observer.observe(socanMarkerLayer, config)
}

function autoReCounterEnviromentTime() {
  if ((window as any).interCountEnvTime) {
    clearInterval((window as any).interCountEnvTime)
    ;(window as any).interCountEnvTime = null
  }

  // Check unless exist info => Cancel counter
  const elementsEnvData = document.getElementsByClassName('enviromentData-timestamp')
  if (!elementsEnvData.length) {
    return
  }

  ;(window as any).interCountEnvTime = setInterval(() => {
    const timeStampEnvDatas = document.getElementsByClassName('enviromentData-timestamp')
    for (const timeStamp of timeStampEnvDatas) {
      timeStamp.innerHTML = timeAgo(timeStamp.getAttribute('data-timestamp'), true) || '-'
    }
  }, 1000)
}

function generate_alert(parentEl) {
  const alertHtml = document.createElement('span')
  alertHtml.classList.add('pretty-pin--infobox-alert')

  alertHtml.innerHTML += '<span class="mdi mdi-alert alert-icon"></span>'
  parentEl.appendChild(alertHtml)
}

function generate_enviroment(parentEl, enviromentInfo: string, envInfoDisplays) {
  const alertHtml = document.createElement('span')
  const enviromentData = parseInfoToString(enviromentInfo) as EnviromentDataModel
  const backgroundPrefixClass = getBgColorCO2(enviromentData.co2)
  const isForceHideEl = envInfoDisplays && envInfoDisplays[enviromentData.san_id] === 0

  alertHtml.classList.add(
    'pretty-pin--environment-alert',
    'map-tooltip',
    'has-text-left',
    `has-text-${backgroundPrefixClass}`,
    isForceHideEl ? 'force-hide' : 'show'
  )
  alertHtml.setAttribute('id', enviromentData.san_id)
  alertHtml.innerHTML += `<span class="envInfoToggle mdi mdi-thermometer-lines alert-icon"></span>`

  const environmentInfo = `
  <div class="tooltiptext has-background-${backgroundPrefixClass} tooltiptext-${backgroundPrefixClass}">
    <p class="pt-2"><b>Temp:</b> ${enviromentData.temp || '---'} °C</p>
    <p><b>Humid:</b> ${enviromentData.hmdty || '---'} %</p>
    <p><b>CO2:</b> ${enviromentData.co2 || '---'} ppm</p>
    <p><b>Illuminance:</b> ${enviromentData.brtns || '---'} Lx</p>
    <p><b>Voltage:</b> ${enviromentData.vltg || '---'} mV</p>
    <br />
    <span class="enviromentData-timestamp" data-timestamp="${enviromentData.created_at}">
      ${timeAgo(enviromentData.created_at, true)}
    </span>
  </div>
`
  alertHtml.innerHTML += environmentInfo

  parentEl.appendChild(alertHtml)
}
